(function () {
  'use strict';

  // Title prefixes are used by the public Rebecca/Colette catalog examples.
  // No standardized tags, metafields or collection naming scheme is assumed.
  var generic = new Set(('a an the and or with for of in by available more colors colours color colour bridal wedding jewelry jewellery accessory accessories set sets piece pieces two three 2 3 silver gold yellow rose white platinum rhodium plated plating k kt karat carat pearl pearls crystal crystals cubic zirconia cz rhinestone rhinestones opal ceramic flower flowers floral vine vines vintage classic elegant delicate romantic minimalist statement necklace necklaces earring earrings bracelet bracelets belt belts sash sashes hair hairpin hairpins haircomb haircombs hairvine hairvines hairpiece hairpieces hairband hairbands comb combs clip clips pin pins headband headbands headdress headdresses headpiece headpieces tiara tiaras crown crowns').split(' '));

  function words(value) {
    return String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  }

  function text(product) {
    return words([product.title, product.type, (product.tags || []).join(' '), String(product.description || '').replace(/<[^>]*>/g, ' ')].join(' '));
  }

  function design(product) {
    var prefix = String(product.title || '').split(/\s+[-–—|:]\s*/)[0];
    var tokens = words(prefix).split(' ').filter(function (token) {
      return token.length > 2 && !generic.has(token) && !/^\d/.test(token);
    });
    return tokens.length && tokens.length <= 3 ? tokens.join(' ') : '';
  }

  function category(product) {
    function identify(value) {
      if (/\b(jewelry|jewellery)\b.*\bset\b|\bnecklace\b.*\bearrings?\b.*\bset\b/.test(value)) return 'set';
      if (/\b(belts?|sash|sashes)\b/.test(value)) return 'belt';
      if (/\bbracelets?\b/.test(value)) return 'bracelet';
      if (/\b(?:hair(?:pins?|combs?|vines?|clips?|pieces?|bands?)?|headdress(?:es)?|headpieces?|headbands?|tiaras?|crowns?|combs?|clips?|pins?|vines?)\b/.test(value)) return 'hair';
      if (/\bearrings?\b/.test(value)) return 'earrings';
      if (/\bnecklaces?\b/.test(value)) return 'necklace';
      return '';
    }
    // A specific hair-pin/belt title takes precedence over a generic type.
    return identify(words(product.title)) || identify(words(product.type));
  }

  function tones(value) {
    var found = [];
    value = words(value);
    if (/\brose gold\b/.test(value)) found.push('rose');
    value = value.replace(/\brose gold\b/g, '');
    if (/\b(silver|rhodium|platinum)\b|\bwhite gold\b/.test(value)) found.push('silver');
    value = value.replace(/\bwhite gold\b/g, '');
    if (/\bgold\b/.test(value)) found.push('yellow');
    return found;
  }

  function karats(value) {
    var found = words(value).match(/\b(?:10|14|18|22|24)\s*(?:k|kt|karat|carat)\b/g) || [];
    return Array.from(new Set(found.map(function (part) { return parseInt(part, 10); })));
  }

  function finish(product, variant) {
    var selected = words((variant.options || []).join(' ') + ' ' + (variant.title || ''));
    var selectedTones = tones(selected);
    var base = text(product);
    var baseTones = tones(base);
    var tone = selectedTones.length === 1 ? selectedTones[0] : (!selectedTones.length && baseTones.length === 1 ? baseTones[0] : '');
    if (!tone) return null;
    var grade = karats(selected);
    if (!grade.length) {
      // Descriptions often list several finishes. Associate a karat only with
      // its adjacent metal, never with every variant of a multi-finish product.
      var metal = tone === 'rose' ? 'rose\\s+gold' : tone === 'yellow' ? '(?:yellow\\s+gold|gold)' : 'white\\s+gold';
      var otherMetal = tone === 'rose' ? '(?:yellow\\s+gold|white\\s+gold)' : tone === 'yellow' ? '(?:rose\\s+gold|white\\s+gold)' : '(?:rose\\s+gold|yellow\\s+gold)';
      function gradePattern(metalName) {
        var unit = '(?:10|14|18|22|24)\\s*(?:k|kt|karat|carat)';
        return new RegExp('\\b' + unit + '\\s+' + metalName + '\\b|\\b' + metalName + '\\s*\\(\\s*' + unit + '\\s*\\)', 'g');
      }
      // Keep punctuation here: stripping "/" or ";" could attach the previous
      // option's trailing karat to the next metal in a description.
      var gradeText = [product.title, product.type, (product.tags || []).join('; '), String(product.description || '').replace(/<\/(?:p|div|li|h[1-6])>/gi, '; ').replace(/<[^>]*>/g, ' ')].join('; ').toLowerCase().replace(/[-–—]/g, ' ');
      // A declared 14K rose-gold finish says nothing about a selected yellow-
      // gold option. Remove only grades explicitly attached to other metals.
      gradeText = gradeText.replace(gradePattern(otherMetal), ' ');
      if (tone === 'yellow') gradeText = gradeText.replace(/\b(?:rose|white)\s+gold/g, 'other-metal');
      var scoped = gradeText.match(gradePattern(metal)) || [];
      grade = karats(scoped.join(' '));
      if (!grade.length && baseTones.length === 1 && baseTones[0] === tone) {
        grade = karats(gradeText);
      } else if (tone !== 'silver' && karats(gradeText.replace(gradePattern(metal), ' ')).length) {
        return null; // Remaining, unscoped gold grades are still uncertain.
      }
    }
    if (grade.length > 1) return null;
    return { tone: tone, karat: grade[0] || null };
  }

  function compatible(source, candidate) {
    return !!source && !!candidate && source.tone === candidate.tone && source.karat === candidate.karat;
  }

  function features(product, variant) {
    var selected = words((variant.options || []).join(' '));
    var value = text(product) + ' ' + selected;
    var definitions = [
      ['pearl', /\bpearls?\b/], ['zirconia', /\bcubic zirconia\b|\bcz\b/],
      ['crystal', /\bcrystals?\b/], ['rhinestone', /\brhinestones?\b/],
      ['opal', /\bopal\b/], ['ceramic', /\bceramic\b/],
      ['floral', /\b(floral|flowers?)\b/], ['vine', /\bvine\b/],
      ['minimalist', /\b(minimalist|simple|delicate)\b/], ['statement', /\bstatement\b/]
    ];
    var selectedMaterials = definitions.slice(0, 6).filter(function (entry) { return entry[1].test(selected); }).map(function (entry) { return entry[0]; });
    return definitions.filter(function (entry, index) {
      return index < 6 && selectedMaterials.length ? selectedMaterials.includes(entry[0]) : entry[1].test(value);
    }).map(function (entry) { return entry[0]; });
  }

  function included(product) {
    var primary = category(product);
    if (primary !== 'set') {
      var components = [primary];
      // A hair-pin set is still just hair; an explicitly bundled headdress +
      // earrings must not receive another pair of earrings from the wider lookup.
      if (primary === 'hair' && /\bearrings?\b/.test(words(product.title)) && /\b(set|with|and)\b|&/i.test(product.title)) components.push('earrings');
      return components;
    }
    var title = words(product.title);
    var details = String(product.description || '').replace(/<[^>]*>/g, ' ').split(/matching[^.!?]{0,100}(?:sold separately|available here)|shipping/i)[0];
    var result = ['set', 'necklace', 'earrings'];
    if (/\bbracelet/.test(title) || /\bbracelet\s*:|\b(?:includes?|with|contains?|comprises?|consists of)\b[^.!?]{0,100}\bbracelet/i.test(details)) result.push('bracelet');
    if (/\b(tiara|hair|crown|headband|comb)\b/.test(title) || /\b(tiara|headband|comb)\s*:|\b(?:includes?|with|contains?|comprises?|consists of)\b[^.!?]{0,100}\b(tiara|headband|comb)/i.test(details)) result.push('hair');
    return result;
  }

  function complementary(a, b) {
    var ca = category(a), cb = category(b);
    if (!ca || !cb || ca === cb) return false;
    if (ca === 'belt' || cb === 'belt') return ['earrings', 'necklace', 'bracelet'].includes(ca === 'belt' ? cb : ca);
    return !included(a).includes(cb) && !included(b).includes(ca);
  }

  function stoneColors(product, variant) {
    // Selected options take precedence over a title listing all color choices.
    function colors(value) {
      return ['ivory', 'white', 'pink', 'blue', 'red', 'black', 'champagne'].filter(function (color) {
        return new RegExp('\\b' + color + '\\b').test(value.replace(/white gold/g, ''));
      });
    }
    // White/ivory ribbon is a sash mounting option, not the stone color.
    var options = (variant.options || []).filter(function (option) { return category(product) !== 'belt' || !/\bribbon\b/i.test(option); });
    var selected = colors(words(options.join(' ')));
    var base = colors(words(product.title));
    return selected.length ? selected : base.length === 1 ? base : [];
  }

  function rank(sources, products, cartIds) {
    var excluded = new Set(cartIds.map(String));
    var recommendations = new Map();
    sources.forEach(function (source) {
      var sourceFinish = finish(source.product, source.variant);
      if (!sourceFinish) return;
      var name = design(source.product);
      var sourceFeatures = features(source.product, source.variant);
      products.forEach(function (product) {
        if (excluded.has(String(product.id)) || !complementary(source.product, product)) return;
        var sharedName = !!name && name === design(product);
        var best = null;
        (product.variants || []).forEach(function (variant) {
          if (!variant.available || !compatible(sourceFinish, finish(product, variant))) return;
          var sourceColors = stoneColors(source.product, source.variant), candidateColors = stoneColors(product, variant);
          if (sourceColors.length && candidateColors.length && !sourceColors.some(function (color) { return candidateColors.includes(color); })) return;
          var candidateFeatures = features(product, variant);
          var score = sourceFeatures.reduce(function (sum, feature) {
            if (feature === 'statement') return sum;
            return sum + (candidateFeatures.includes(feature) ? (['floral', 'vine', 'minimalist', 'statement'].includes(feature) ? 2 : 3) : 0);
          }, 0);
          if (sourceFeatures.includes('statement') && candidateFeatures.includes('minimalist')) score += 1;
          if (!sharedName && score < 3) return;
          // Variant IDs and product handles provide deterministic tie breaks.
          var match = { product: product, variant: variant, sharedName: sharedName, score: score };
          if (!best || score > best.score || (score === best.score && Number(variant.id) < Number(best.variant.id))) best = match;
        });
        if (!best) return;
        var previous = recommendations.get(String(product.id));
        if (!previous || Number(best.sharedName) > Number(previous.sharedName) || (best.sharedName === previous.sharedName && best.score > previous.score)) recommendations.set(String(product.id), best);
      });
    });
    return Array.from(recommendations.values()).sort(function (a, b) {
      return Number(b.sharedName) - Number(a.sharedName) || b.score - a.score || String(a.product.handle).localeCompare(String(b.product.handle), 'en') || Number(a.product.id) - Number(b.product.id);
    }).slice(0, 10);
  }

  function queries(source) {
    var name = design(source.product);
    var signals = features(source.product, source.variant);
    var materials = signals.filter(function (feature) { return ['pearl', 'zirconia', 'crystal', 'rhinestone', 'opal', 'ceramic'].includes(feature); });
    var terms = (materials.length ? materials : signals.filter(function (feature) { return feature !== 'statement'; })).slice(0, 3).map(function (feature) {
      return feature === 'zirconia' ? '("cubic zirconia" OR cz)' : feature;
    });
    var targets = {
      set: [['bracelet'], ['hair']], bracelet: [['set', 'hair', 'belt']],
      hair: [['earrings', 'necklace'], ['bracelet', 'set']],
      belt: [['earrings', 'necklace'], ['bracelet']],
      earrings: [['bracelet', 'hair', 'necklace', 'belt']], necklace: [['bracelet', 'hair', 'earrings', 'belt']]
    }[category(source.product)] || [];
    var fields = {
      set: '((title:jewelry OR title:jewellery) AND title:set)',
      bracelet: 'title:bracelet*', earrings: 'title:earring*', necklace: 'title:necklace*',
      belt: '(title:belt* OR title:sash*)',
      hair: '(title:hair* OR title:comb* OR title:vine* OR title:head* OR title:tiara* OR title:crown*)'
    };
    var reserved = included(source.product);
    var selectedFinish = finish(source.product, source.variant);
    var finishHint = selectedFinish && {
      rose: '"rose gold"', yellow: 'gold', silver: '(silver OR rhodium OR platinum OR "white gold")'
    }[selectedFinish.tone];
    return {
      name: name ? name.split(' ').map(function (token) { return 'title:' + token; }).join(' ') : '',
      // Separate groups prevent one category from filling the first page.
      // Finish hints improve retrieval; variant-level guards remain decisive.
      fallbacks: terms.length ? targets.map(function (group) {
        var categories = group.filter(function (target) { return !reserved.includes(target); });
        return categories.length ? '(' + terms.join(' OR ') + ') (' + categories.map(function (target) { return fields[target]; }).join(' OR ') + ')' + (finishHint ? ' ' + finishHint : '') : '';
      }).filter(Boolean) : []
    };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { design: design, finish: finish, compatible: compatible, category: category, rank: rank, queries: queries };
    return;
  }

  var drawer = document.getElementById('cart-drawer');
  if (!drawer || drawer.adoraMatchesInitialized) return;
  drawer.adoraMatchesInitialized = true;
  var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var cache = new Map();
  var queue = [];
  var activeRequests = 0;
  var generation = 0;
  var timer;
  var runningSignature = '';
  var renderedAt = 0;
  // Keep the learned affordance dismissed across drawer replacements/reopens
  // for this page, just like the product gallery's first-swipe prompt.
  var hasExplored = false;

  function pump() {
    while (activeRequests < 3 && queue.length) {
      activeRequests++;
      queue.shift()();
    }
  }

  function json(url, cached, scope) {
    var key = scope + '|' + url;
    var entry = cache.get(key);
    if (entry && (entry.pending || (cached && entry.expires > Date.now()))) return entry.promise;
    var promise = new Promise(function (resolve, reject) {
      queue.push(function () {
        var controller = new AbortController();
        var timeout = setTimeout(function () { controller.abort(); }, 7000);
        fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' }, cache: 'no-store', signal: controller.signal })
          .then(function (response) { if (!response.ok) throw new Error('Lookup failed'); return response.json(); })
          .then(resolve, reject)
          .finally(function () { clearTimeout(timeout); activeRequests--; pump(); });
      });
      pump();
    });
    entry = { pending: true, expires: 0, promise: promise };
    cache.set(key, entry);
    promise.then(function () {
      entry.pending = false;
      entry.expires = Date.now() + 60000;
      if (!cached) cache.delete(key);
      // Keep every in-flight promise so overlapping refreshes share requests.
      cache.forEach(function (value, cacheKey) { if (cache.size > 80 && !value.pending) cache.delete(cacheKey); });
    }, function () { if (cache.get(key) === entry) cache.delete(key); });
    return promise;
  }

  function open() { return drawer.getAttribute('aria-hidden') === 'false'; }
  function busy() { return !!(drawer.adoraCartState && drawer.adoraCartState.busy); }
  function section() { return drawer.querySelector('[data-cart-matches]'); }
  function hide() { var el = section(); if (el) el.hidden = true; }
  function signature() {
    return Array.from(drawer.querySelectorAll('[data-cart-item]')).map(function (el) { return el.dataset.key + ':' + el.dataset.variantId + ':' + el.querySelector('[data-qty]')?.textContent; }).join('|') + ':' + ((drawer.adoraCartState || {}).revision || 0);
  }
  function current(version, state) { return generation === version && open() && !busy() && signature() === state; }

  function updateNavigation() {
    var el = section();
    if (!el || el.hidden) return;
    var track = el.querySelector('[data-match-track]');
    var overflow = track.scrollWidth > track.clientWidth + 2;
    var navigation = el.querySelector('[data-match-navigation]');
    navigation.hidden = !overflow;
    el.querySelector('[data-match-hint]').hidden = !overflow || hasExplored;
    navigation.querySelector('[data-match-direction="-1"]').disabled = track.scrollLeft <= 2;
    navigation.querySelector('[data-match-direction="1"]').disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
  }

  function render(matches) {
    var el = section();
    if (!el) return;
    var track = el.querySelector('[data-match-track]');
    var list = track.querySelector('[data-match-list]');
    var fragment = document.createDocumentFragment();
    matches.forEach(function (match) {
      var product = match.product, variant = match.variant;
      if (!variant.priceText) return;
      var url = new URL(product.url || root + 'products/' + encodeURIComponent(product.handle), location.origin);
      if (url.origin !== location.origin || !url.pathname.includes('/products/')) return;
      url.searchParams.set('variant', String(variant.id));
      var card = document.createElement('li');
      var link = document.createElement('a');
      link.href = url.pathname + url.search;
      if (variant.image) {
        var imageURL = new URL(variant.image, location.origin);
        if (imageURL.protocol === 'https:' || imageURL.origin === location.origin) {
          var image = document.createElement('img');
          image.src = imageURL.href;
          image.alt = variant.imageAlt || product.title + ' — ' + variant.title;
          image.loading = 'lazy';
          image.width = 120; image.height = 88;
          link.appendChild(image);
        }
      }
      var title = document.createElement('span');
      title.className = 'cart-matches__name';
      title.textContent = product.title;
      var price = document.createElement('span');
      price.className = 'cart-matches__price';
      // Liquid money formats may contain entities (for example &nbsp;).
      // Decode in an inert textarea, then insert only text into the card.
      var moneyText = document.createElement('textarea');
      moneyText.innerHTML = variant.priceText;
      price.textContent = moneyText.value;
      link.appendChild(title); link.appendChild(price); card.appendChild(link); fragment.appendChild(card);
    });
    list.replaceChildren(fragment);
    track.scrollLeft = 0;
    el.hidden = !list.children.length;
    requestAnimationFrame(updateNavigation);
  }

  function search(query, scope, currency) {
    var params = new URLSearchParams({ view: 'cart-matches', type: 'product', q: query, 'options[unavailable_products]': 'hide', 'options[prefix]': 'none' });
    return json(root + 'search?' + params, true, scope).then(function (data) {
      if (data.currency !== currency || !Array.isArray(data.products)) throw new Error('Unexpected lookup context');
      return data.products;
    });
  }

  async function refresh() {
    if (!open() || busy()) return;
    var state = signature();
    if (runningSignature === state && Date.now() - renderedAt < 60000) return;
    runningSignature = state;
    renderedAt = Date.now();
    var version = generation;
    try {
      var cart = await json(root + 'cart.js', false, 'cart|' + state);
      if (!current(version, state)) return;
      if (!cart.items || !cart.items.length) { hide(); return; }
      var scope = root + '|' + cart.currency + '|' + document.documentElement.lang;
      var handles = Array.from(new Set(cart.items.map(function (item) { return item.handle || (item.url || '').split('/products/')[1]?.split('?')[0]; }).filter(Boolean)));
      var products = await Promise.all(handles.map(function (handle) { return json(root + 'products/' + encodeURIComponent(handle) + '.js', true, scope); }));
      if (!current(version, state)) return;
      var sources = cart.items.map(function (item) {
        var product = products.find(function (entry) { return String(entry.id) === String(item.product_id); });
        var variant = product && product.variants.find(function (entry) { return String(entry.id) === String(item.variant_id); });
        return product && variant ? { product: product, variant: variant } : null;
      }).filter(Boolean);
      var plans = sources.map(queries);
      var names = Array.from(new Set(plans.map(function (plan) { return plan.name; }).filter(Boolean)));
      var candidates = (await Promise.all(names.map(function (query) { return search(query, scope, cart.currency); }))).flat();
      if (!current(version, state)) return;
      var cartIds = cart.items.map(function (item) { return item.product_id; });
      var matches = rank(sources, candidates, cartIds);
      if (matches.length < 10) {
        var fallbacks = Array.from(new Set(plans.flatMap(function (plan) { return plan.fallbacks; })));
        candidates = candidates.concat((await Promise.all(fallbacks.map(function (query) { return search(query, scope, cart.currency); }))).flat());
        matches = rank(sources, candidates, cartIds);
      }
      if (current(version, state)) render(matches);
    } catch (_error) {
      if (current(version, state)) { hide(); runningSignature = ''; }
    }
  }

  function schedule() {
    generation++;
    runningSignature = '';
    clearTimeout(timer);
    hide();
    if (open() && !busy()) timer = setTimeout(refresh, 60);
  }

  // Observe the existing stable drawer lifecycle; no cart mutation, refresh,
  // discount, money formatter or checkout handler is replaced or intercepted.
  var observer = new MutationObserver(function (records) {
    if (records.some(function (record) {
      return !record.target.closest('[data-cart-matches]') && (record.type === 'attributes' || record.target === drawer || drawer.querySelector('[data-cart-drawer-body]')?.contains(record.target));
    })) schedule();
  });
  observer.observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'aria-busy', 'data-key', 'data-variant-id'] });
  drawer.addEventListener('adora:cart-idle', schedule);
  drawer.addEventListener('scroll', function (event) {
    if (!event.target.matches('[data-match-track]')) return;
    // A native leftward swipe moves the track right. Vertical cart scrolling
    // and the render-time reset to zero do not dismiss the prompt.
    if (event.target.scrollLeft > 8) hasExplored = true;
    updateNavigation();
  }, true);
  drawer.addEventListener('click', function (event) {
    var button = event.target.closest('[data-match-direction]');
    if (!button) return;
    var track = section().querySelector('[data-match-track]');
    if (button.hasAttribute('data-match-hint')) {
      hasExplored = true;
      track.focus({ preventScroll: true });
      updateNavigation();
    }
    track.scrollBy({ left: Number(button.dataset.matchDirection) * (track.clientWidth + 12), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  });
  drawer.addEventListener('keydown', function (event) {
    if (!event.target.matches('[data-match-track]') || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    var track = event.target;
    var left = event.key === 'Home' ? 0 : event.key === 'End' ? track.scrollWidth : track.scrollLeft + (event.key === 'ArrowLeft' ? -1 : 1) * (track.clientWidth + 12);
    track.scrollTo({ left: left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
  });
  if (window.ResizeObserver) new ResizeObserver(updateNavigation).observe(drawer);
  if (open()) schedule();
})();
